import { Group, Select, Stack, Tabs, Text } from '@mantine/core';
import { useState } from 'react';
import TextInput from 'src/components/design-system/input/TextInput';
import ConversationViewer from 'src/secret-menu/conversation-debugger/ConversationViewer';
import { useCompanyUsers } from 'src/secret-menu/conversation-debugger/hooks/useCompanyUsers';
import { useConversationSummaries } from 'src/secret-menu/conversation-debugger/hooks/useConversationSummaries';
import { ConversationSummary } from 'src/secret-menu/conversation-debugger/types';
import { Company, useCompanies } from 'src/secret-menu/hooks/useCompanies';

function conversationLabel(conversation: ConversationSummary): string {
  const name = conversation.title ?? conversation.firstUserMessage ?? '(untitled)';
  const scope = conversation.clientName ?? conversation.familyName ?? conversation.originFamilyName;
  const parts = [
    name,
    conversation.source === 'CHAT' ? null : conversation.source,
    scope,
    new Date(conversation.updatedAt).toLocaleString(),
    `${conversation.messageCount} msgs`,
  ];
  return parts.filter(Boolean).join(' · ');
}

function BrowseTab() {
  const { companies, isLoading: companiesLoading } = useCompanies();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const { users } = useCompanyUsers(selectedCompany?.id ?? null);
  const {
    conversations,
    isLoading: conversationsLoading,
    isError: conversationsError,
  } = useConversationSummaries(selectedUserId, selectedCompany?.tenantName ?? null);

  const companyOptions = (companies ?? []).map(c => ({
    value: c.id,
    label: `${c.name} (${c.tenantName})`,
  }));

  const userOptions = (users ?? []).map(u => ({
    value: u.id,
    label: `${u.firstName} ${u.lastName} (${u.emailAddress})`,
  }));

  const conversationOptions = (conversations ?? []).map(c => ({
    value: c.id,
    label: conversationLabel(c),
  }));

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label="Company"
          placeholder="Select a company"
          data={companyOptions}
          searchable
          clearable
          disabled={companiesLoading}
          value={selectedCompany?.id ?? null}
          onChange={val => {
            setSelectedCompany(companies?.find(c => c.id === val) ?? null);
            setSelectedUserId(null);
            setSelectedConversationId(null);
          }}
        />
        <Select
          label="User"
          placeholder="Select a user"
          data={userOptions}
          searchable
          clearable
          disabled={!selectedCompany}
          value={selectedUserId}
          onChange={val => {
            setSelectedUserId(val);
            setSelectedConversationId(null);
          }}
        />
        <Select
          label="Conversation"
          placeholder={conversationsLoading ? 'Loading conversations…' : 'Select a conversation'}
          data={conversationOptions}
          searchable
          clearable
          disabled={!selectedUserId || conversationsLoading}
          value={selectedConversationId}
          onChange={setSelectedConversationId}
        />
      </Group>

      {conversationsError && (
        <Text size="textSm" c="red">
          Failed to load conversations for this user.
        </Text>
      )}
      {selectedUserId && conversations && conversations.length === 0 && (
        <Text size="textSm" c="dimmed">
          No conversations found for this user.
        </Text>
      )}

      {selectedConversationId && selectedCompany && (
        <ConversationViewer
          key={selectedConversationId}
          conversationId={selectedConversationId}
          tenantName={selectedCompany.tenantName}
        />
      )}
    </Stack>
  );
}

function SearchTab() {
  const [conversationId, setConversationId] = useState('');
  const [tenantName, setTenantName] = useState('');
  const { companies } = useCompanies();

  const companyOptions = (companies ?? []).map(c => ({
    value: c.tenantName,
    label: `${c.name} (${c.tenantName})`,
  }));

  const canSearch = conversationId.length === 36 && tenantName.length > 0;

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label="Tenant"
          placeholder="Select tenant"
          data={companyOptions}
          searchable
          value={tenantName || null}
          onChange={val => setTenantName(val ?? '')}
        />
        <TextInput
          label="Conversation ID"
          placeholder="Paste a conversation UUID"
          value={conversationId}
          onChange={e => setConversationId(e.currentTarget.value.trim())}
        />
      </Group>

      {canSearch && (
        <ConversationViewer
          key={conversationId}
          conversationId={conversationId}
          tenantName={tenantName}
        />
      )}
    </Stack>
  );
}

export default function ConversationDebuggerTab() {
  return (
    <Stack p="md" gap="md">
      <Tabs defaultValue="browse">
        <Tabs.List>
          <Tabs.Tab value="browse">Browse</Tabs.Tab>
          <Tabs.Tab value="search">Search by ID</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="browse" pt="md">
          <BrowseTab />
        </Tabs.Panel>
        <Tabs.Panel value="search" pt="md">
          <SearchTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
